import {HTTP} from "@/http";

export class AccountService {
  basePath = 'api/v2/accounts';

  constructor() {
  }

  /**
   * Get current user account
   * @returns {Promise<Object>} Promise that resolves to the current user account
   */
  getMyAccount() {
    return HTTP.get(`${this.basePath}/self`);
  }

  /**
   * Set a password with reset code
   * @param {string} code - The reset code
   * @param {string} password - The new password
   * @returns {Promise<Object>} Promise that resolves when the password is set
   */
  setPassword(code, password) {
    const data = {
      code, password
    };
    return HTTP.post(`${this.basePath}/password`, data);
  }

  /**
   * Update current user settings
   * @param {Object} settings - The settings to update
   * @returns {Promise<Object>} Promise that resolves to the updated account
   */
  updateSelfSettings(settings) {
    return HTTP.patch(`${this.basePath}/self/settings`, settings);
  }

  /**
   * Delete current user account
   * @returns {Promise<Object>} Promise that resolves when the account is deleted
   */
  deleteSelf() {
    return HTTP.delete(`${this.basePath}/self`);
  }

  /**
   * Reset current user password
   * @returns {Promise<Object>} Promise that resolves when the password reset is requested
   */
  resetPassword() {
    return HTTP.post(`${this.basePath}/self/password/reset`);
  }

  /**
   * Add a device token to current user account
   * @param {string} token - The device token to add
   * @returns {Promise<Object>} Promise that resolves to the updated account
   */
  addDeviceToken(token) {
    return HTTP.post(`${this.basePath}/self/deviceToken`, { token });
  }

  /**
   * Delete a device token from current user account
   * @param {string} token - The device token to delete
   * @returns {Promise<Object>} Promise that resolves to the updated account
   */
  deleteDeviceToken(token) {
    return HTTP.delete(`${this.basePath}/self/deviceToken`, { data: { token } });
  }

  /**
   * Query accounts by customer ID (this endpoint is in the customers controller)
   * @param {string} customerId - The customer ID
   * @param {string} search - Search term
   * @param {boolean} localOnly - Filter for local accounts only
   * @param {Object} page - Pagination parameters
   * @returns {Promise<Object>} Promise that resolves to the paginated accounts
   */
  queryAccountsByCustomerId(customerId, search, localOnly, page) {
    const queryParams = {
      customerId: customerId,
      search: search,
      localOnly: localOnly,
      ...page
    };
    return HTTP.get(`api/v2/customers/${customerId}/accounts`, {params: queryParams})
  }

  /**
   * Assign a customer to an account (this endpoint is in the customers controller)
   * @param {string} customerId - The customer ID
   * @param {string} accountId - The account ID
   * @returns {Promise<Object>} Promise that resolves when the customer is assigned
   */
  assignCustomerToAccount(customerId, accountId) {
    return HTTP.post(`api/v2/customers/${customerId}/accounts/${accountId}`);
  }
}

/**
 * @type {AccountService}
 */
export const accountService = new AccountService();
